/**
 * @fileoverview Public practice-clips page, reached from a summary email link.
 *
 * This is the delivery path that makes clips reachable by everyone the summary
 * reaches — including the parent of an under-13 student, who holds no Hans
 * account and never will. Authorization is the token in the URL, the same
 * bearer model as the public consent and revoke pages, and the consent text
 * discloses it in as many words.
 *
 * Uses a BARE axios instance, not `lib/api`: the shared client's 401
 * interceptor would bounce a parent with no account to the instructor login.
 * Same reason `BookingPage` does it.
 *
 * The token outlives the clips on purpose, so "no clips" is a normal, expected
 * state here — after 30 days, or after a consent withdrawal — and gets an
 * honest explanation rather than an error.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "@/App";

const formatClipLength = (seconds) => {
  const total = Math.max(0, Math.round(seconds || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const ClipsPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${API}/clips/${token}`, { signal: controller.signal })
      .then((res) => setState({ loading: false, data: res.data, error: false }))
      .catch(() => {
        if (controller.signal.aborted) return;
        setState({ loading: false, data: null, error: true });
      });
    return () => controller.abort();
  }, [token]);

  if (state.loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[var(--studio-base)]"
        data-theme="dark"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-tertiary)]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[var(--studio-base)] px-6"
        data-theme="dark"
      >
        <div className="max-w-[420px] text-center">
          <h1 className="font-serif text-[26px] leading-tight text-[var(--text-primary)]">
            This link is no longer valid
          </h1>
          <p className="mt-3 font-sans text-[14px] leading-relaxed text-[var(--text-tertiary)]">
            Practice clips are deleted automatically 30 days after a lesson, and are removed straight away if recording
            consent is withdrawn. Ask your teacher if you need them again.
          </p>
        </div>
      </div>
    );
  }

  const { lesson_title: lessonTitle, teacher_name: teacherName, clips = [] } = state.data || {};

  return (
    <div
      className="min-h-screen bg-[var(--studio-base)] px-6 py-16"
      data-theme="dark"
    >
      <div className="mx-auto max-w-[620px]">
        <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
          Practice clips
        </span>
        <h1 className="mt-2 font-serif text-[30px] leading-tight tracking-[-0.01em] text-[var(--text-primary)]">
          {lessonTitle}
        </h1>
        <p className="mt-2 font-sans text-[14px] text-[var(--text-tertiary)]">with {teacherName}</p>

        {clips.length === 0 ? (
          <p className="mt-10 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
            These clips have been deleted. Practice clips are kept for 30 days after a lesson, and are removed
            immediately if recording consent is withdrawn.
          </p>
        ) : (
          <>
            <p className="mt-6 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
              Short excerpts from the moments music was played in this lesson, so you can hear how it should sound while
              practising. They are deleted automatically 30 days after the lesson.
            </p>
            <div className="mt-8 flex flex-col gap-6">
              {clips.map((clip) => (
                <div key={clip.clip_id} className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-sans text-[14px] leading-snug text-[var(--text-secondary)]">
                      {clip.label || (clip.speaker ? `${clip.speaker} demonstration` : "Demonstration")}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-[var(--text-tertiary)]">
                      {formatClipLength(clip.duration_seconds)}
                    </span>
                  </div>
                  <audio controls preload="none" src={clip.url} className="w-full" data-testid="clip-audio">
                    Your browser cannot play this clip.
                  </audio>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClipsPage;
